import React, { useContext, useState } from 'react';
import _ from 'lodash';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

import submenuModels from './menus/submenu-models';
import CRUDPage from './CRUDPage';
import NiceDate from '../components/common/NiceDate';
import { ImagePreview } from '../components/images/ImagePreview';
import MongoDocEditor from '../components/lego/MongoDocEditor';
import ModelContextEditor from '../components/lego/ModelContextEditor';
import ModelIdSelector from '../components/lego/ModelIdSelector';
import { SingleImageUrlEditor } from '../components/common/images/SingleImageUrlEditor';
import { StateBadge } from '../components/data-issues/StateBadge';
import LegoContextSummary from '../components/lego/LegoContextSummary';
import CountryFlag from '../components/common/CountryFlag';
import TrimSpec from '../components/lego/trims/TrimSpec';
import MultiCountryEditor from '../components/common/editors/MultiCountryEditor';
import LegoAdminPageContext from './legoAdminPageContext';
import NotesEditor from '../components/common/editors/NotesEditor';
import { Icon } from '../components/common/Icon';
import { IconButton } from '../components/common/IconButton';
import { SalesByYearMiniature } from '../components/lego/SalesByYearMiniature';
import { LegoFiltersBar } from '../components/common/LegoFiltersBar';
import { SwitchInput } from '../components/common/SwitchInput';
import LegoLabel from '../components/lego/LegoLabel';
import LegoLabelsSelector from '../components/lego/LegoLabelsSelector';
import SingleValueSelector from '../components/common/editors/SingleValueSelector';
import MultilineTextEditor from '../components/common/editors/MultilineTextEditor';
import StableUrlsWidget from '../components/lego/stable-urls/StableUrlsWidget';

function SelectModelIdsModal({ defaultModelIds = [], defaultExtraLlmInstruction = '', onFindIdentical, onOk, onCancel }) {
  const [modelIds, setModelIds] = useState(defaultModelIds?.length ? defaultModelIds : []);
  const [extraLlmInstruction, setExtraLlmInstruction] = useState(defaultExtraLlmInstruction || '');
  const [findingIdentical, setFindingIdentical] = useState(false);

  const handleFindIdentical = async () => {
    if (!modelIds?.length || !onFindIdentical) return;
    setFindingIdentical(true);
    try {
      const found = await onFindIdentical(modelIds);
      if (Array.isArray(found) && found.length > 0) {
        setModelIds(found);
      }
    } finally {
      setFindingIdentical(false);
    }
  };

  return <div style={{ minWidth: '520px' }}>
      <div className="mb-2">
        Select related model IDs to generate model gens by country for.
      </div>

      <ModelIdSelector value={modelIds} onChange={setModelIds} multi={true} />

      <div className="mt-3">
        <IconButton icon="search" level="success" fill small className="mr-2" disabled={!modelIds?.length || findingIdentical} onClick={handleFindIdentical}>
          {findingIdentical ? 'Finding...' : 'Find identical'}
        </IconButton>
      </div>

      <div className="mt-3">
        <label className="form-label">Extra LLM instruction (optional)</label>
        <MultilineTextEditor value={extraLlmInstruction} onChange={setExtraLlmInstruction} minRows={2} maxRows={6}/>
      </div>

      <div className="mt-3">
        <button className="btn btn-primary mr-2" disabled={!modelIds?.length} onClick={() => onOk({ modelIds, extraLlmInstruction: extraLlmInstruction.trim() })}>
          Generate
        </button>
        <button className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>;
}

function GenerationsFiltersBar({ filter, onChange }) {
  const { page } = useContext(LegoAdminPageContext)

  const refreshModeloCaches = () => page.runAsync(page.service('/services/data/models').update({}, {}));
  const onFieldChange = (field, value) => {
    let newFilter = _.cloneDeep(filter || {});

    if (field === 'modelId') {
      delete newFilter['generations.genId'];
      delete newFilter.modelId;

      let { genId, modelId, make } = value || {};
      if (modelId) {
        newFilter.modelId = modelId;
      } else if (make) {
        newFilter.modelId = { $regex: '^' + make };
      }

      if (genId) {
        newFilter['generations.genId'] = genId;
      }
    } else {
      if (_.isEmpty(value)) {
        delete newFilter[field];
      } else {
        newFilter[field] = _.cloneDeep(value);
      }
    }

    onChange(newFilter);
  };

  let { modelId, 'generations.genId': genId } = filter || {};

  let context = {};
  if (modelId?.$regex) {
    context = { make: modelId.$regex.slice(1) }
  } else if (modelId) {
    context = { modelId }
  }
  if (genId) {
    context.genId = genId
  }

  const filterFields = ['countries', 'labels'];

  const generationExists = filter.generations?.$exists === true;
  const withSales = filter.salesByYear?.$exists;

  return <div className={'d-flex align-items-center'} style={{ gap: '10px'}}>
    <span style={{ width: '280px', display: 'inline-block' }} className={'zoom-75'}>
      <ModelContextEditor value={context} onChange={(ctx) => onFieldChange('modelId', ctx)} />
    </span>

    <div className='pr-3'>
      <div>
        <SwitchInput value={generationExists} onChange={v => onFieldChange('generations', v ? { $exists: true, $ne: [] } : undefined)}>With gens.</SwitchInput>
      </div>
      <div>
        <SwitchInput triState value={withSales} onChange={v => onFieldChange('salesByYear', v !== undefined ? { $exists: v, $ne: {} } : undefined)}>With/out sales</SwitchInput>
      </div>
    </div>

    <LegoFiltersBar fields={filterFields} filter={filter} onChange={onChange} />

    <span className={'btn btn-sm btn-warning'} onClick={refreshModeloCaches}>Refresh site</span>

    <StableUrlsWidget/>
  </div>;
}

export default class GenerationsByCountryManager extends CRUDPage {
  constructor(props) {
    super(props, '/services/data/models/gensbycountry');

    this.submenu = submenuModels;
    this.state.columnSort = { index: 1, asc: true, sorter: ['modelId'] }
    this.newObjectTemplate = {
      countries: [],
      modelId: null,
      specs: null,
      generations: [
        {
          genId: "",
          name: "",
          fromYear: 0,
          toYear: 0,
        }
      ],
      state: 'inprogress'
    }


    this.modelListService = this.service('/services/data/models')
  }

  getFacetFiltersComponent() {
    return (
      <>
        <div className="d-flex align-items-center">
          <GenerationsFiltersBar onChange={this.onFiltersChange.bind(this)} filter={this.state.facetFilters} />
          <IconButton icon="assistant" fill level="violeta" small className="ml-2" onClick={this.openAiCountryGens.bind(this)}>AI Gens...</IconButton>
        </div>
      </>
    );
  }

  async openAiCountryGens() {
    const aiSvc = this.service('/services/ai/build-model-generations-by-country');
    const filter = this.state?.facetFilters || {};
    const defaultGroup = filter.modelId && !filter.modelId.$regex ? [filter.modelId] : [];

    const group = await new Promise((resolve) => {
      let resolved = false;
      const onResolve = (value) => {
        resolved = true;
        resolve(value);
        this.closeModal();
      };

      const onFindIdentical = (modelIds) =>
        this.runAsync(aiSvc.find({ query: { modelIds } }), 'Finding identical related models...');

      this.openModal(
        <SelectModelIdsModal defaultModelIds={defaultGroup} defaultExtraLlmInstruction="" onFindIdentical={onFindIdentical} onOk={onResolve} onCancel={() => onResolve(null)}/>,
        {
          title: 'Create AI model gens by country',
          minSize: false,
          onClose: () => !resolved && resolve(null),
        },
      );
    });

    if (!group?.modelIds?.length) return;

    const { modelIds, extraLlmInstruction } = group;
    const docs = await this.runAsync(
      aiSvc.create({ group: modelIds, extraLlmInstruction: extraLlmInstruction || undefined }),
      `Generating model gens by country for ${modelIds.join(', ')}...`,
    );

    if (Array.isArray(docs) && docs.length > 0) {
      this.importJSON(docs);
      this.showNotification({
        level: 'success',
        title: 'AI country gens loaded',
        body: `Loaded ${docs.length} modelGensByCountry document(s). Review them and click “Save changes”.`,
        time: new Date(),
      });
    } else {
      await this.alert('No documents were returned.', 'AI country gens');
    }
  }

  getColumnsDefinition(objects) {
    return [
      { content: 'Last update', className: 'text-center', sorter: 'updatedAt' },
      { content: 'Model', className: 'text-center', sorter: ['modelId'] },
      { content: 'Countries', className: 'text-center' },
      { content: '', className: 'text-center' },
      { content: 'Sales', className: 'text-center', sorter: doc => doc.salesByYear ? _.sumBy(_.values(doc.salesByYear), d => d.sales || 0) : 0 },
      { content: 'Generations', className: 'text-center' },
    ];
  }

  getRowClasses(obj, i, rows) {
    const { modelId } = obj;
    let prevRowModelId = rows[i-1]?.modelId;
    return (prevRowModelId !== modelId ? 'row-border-top-3' : '');
  }

  getObjectColumns(generation, def, i, allRows) {
    let {
      modelId, submodelOf, countries, generations, salesByYear, specs, state, notes, updatedAt, createdAt, updatedBy, soldFromYear, soldToYear, soldIntervals, labels
    } = generation;

    let genColors = {
      '1': 'bg-light-success',
      '2': 'bg-light-partial-success',
      '3': 'bg-patito',
      '4': 'bg-light-warning',
      '5': 'bg-light-ladrillo',
      '6': 'bg-light-rosa',
      '7': 'bg-light-violeta',
    }

    return [
      <td key={'id'}>
        {updatedAt && <NiceDate t={updatedAt} />}
        <br />
        <StateBadge state={state} />
        { labels?.length ? _.map(labels, l => <LegoLabel label={l} key={l} className={'ml-1'}/>) : null }
      </td>,

      <td key={'modelId'} className={'text-left'}>
        <div>
          <LegoContextSummary context={{ modelId, ...specs }} />
        </div>
        {submodelOf ? <div>Submodel of: <TrimSpec spec={'modelId'} value={submodelOf} /> </div> : null}
        <div className={'text-info monospace'}>
          {soldFromYear ? (soldFromYear || '') + ' - ' : ''} {soldToYear ? soldToYear : (soldFromYear ? 'present' : '')}
        </div>
      </td>,

      <td key={'countries'} className={'text-center'}>
        {countries.map(code => code === '*' ?
          <span key={'*'} className={'text-primary mx-1'}>✱</span>
          :
          <CountryFlag key={code} countryCode={code} />)}
      </td>,

      <td key={'note'}>{notes?.length ? <Icon icon={'chat'} level={'primary'} /> : null}</td>,

      <td key={'sales'}>
        <SalesByYearMiniature salesByYear={salesByYear} soldIntervals={soldIntervals} />
      </td>,

      <td key={'context'}>
        <div className={'text-left'}>
          {generations.map(({ genId, name, fromYear, toYear, photo, specs }) => {
            let genColorClass = genColors[(genId && genId.match(/@[^@]+@G(\d+).*$/)?.[1])] || 'bg-light-secondary';

            return <div key={genId} className={'p-1 ' + genColorClass}>
              <LegoContextSummary inline context={{ genId, ...specs }} className={'inline-block'} />
              <span className={'ml-2 monospace'}>{name || ''}</span>
              {fromYear ? <span className={'ml-2 monospace text-info'}>{fromYear}</span> : null}
              {toYear ? <span className={'monospace text-info'}> - {toYear}</span> : null}
              {photo?.url ? <ImagePreview className={'ml-2'} img50 zoom={true} url={photo?.url} /> : null}
            </div>;
          })}
        </div>
      </td>,

    ];
  }

  getObjectEditorComponent(obj, defaultOnSaveCbk, defaultOnCancelCbk) {
    let genIdentifier = obj?.modelId || 'country_vehicle';
    let schema = {
      modelId: (value, onChange) => <ModelContextEditor value={value ? { modelId: value } : null} onChange={ctx => onChange(ctx?.modelId)} />,
      countries: (value, onChange) => <MultiCountryEditor value={value} onChange={onChange} />,
      specs: (value, onChange) => <ModelContextEditor value={value} onChange={ctx => onChange(ctx)} />,
      genId: (value, onChange) => <ModelContextEditor value={value ? { genId: value } : null} onChange={ctx => onChange(ctx?.genId)} />,
      wasSold: 'boolean',
      soldFromYear: 'number',
      soldToYear: 'number',
      fromYear: 'number',
      toYear: 'number',
      soldIntervals: 'array',
      labels: (value, onChange) => <LegoLabelsSelector value={value} onChange={onChange} />,
      photo: (value, onChange) => <SingleImageUrlEditor folder={'model-gen-pictures'}
        category={'generation-picture'}
        value={value} defaultName={genIdentifier}
        onChange={onChange} />,
        submodelOf: (value, onChange) => <ModelContextEditor value={value ? { modelId: value } : null} onChange={ctx => onChange(ctx?.modelId)} />,
      generations: 'array',
      notes: (value, onChange) => <NotesEditor value={value} onChange={onChange} />,
      'salesByYear.$.source': (value, onChange) => <SingleValueSelector className={'zoom-75'} value={value} options={['sales-third-party', 'checked-manually']} onChange={onChange} />
    }

    let onSave = (changedObj, ...args) => {
      if (!changedObj.countries?.length) {
        return this.handleError('There must be at least one country')
      }
      if (!changedObj.modelId?.length) {
        return this.handleError('modelId is required')
      }
      if (!_.every(changedObj.generations, gen => gen.genId)) {
        return this.handleError('genId is required');
      }

      // Remove empty name, fromYear, toYear in generations
      changedObj.generations = changedObj.generations.map(gen => _.pickBy(gen));

      return defaultOnSaveCbk(changedObj, ...args);
    }

    return <MongoDocEditor extendSchema={schema} showFields={['specs', 'countries', 'modelId', 'wasSold','soldFromYear', 'soldToYear', 'soldIntervals', 'salesByYear', 'generations', 'notes', 'labels']} obj={obj} onSave={onSave} onCancel={defaultOnCancelCbk} />
  }

  groupObjectsInRows(objects) {
    let columns = _.uniq(_.flatten(_.flatMap(objects, o => o.generations.map(g => g.genId)))).sort().reverse();

    let columnsIndexes = {};

    for (let i = 0; i < columns.length; i++) {
      columnsIndexes[columns[i]] = i;
    }

    let rows = {};
    _.each(objects, o => o.countries.forEach(c => (rows[c] = rows[c] || []).push(o)));

    let matrix = _.mapValues(rows, (objs, country) => {
      let values = _.map(columns, () => null);
      _.each(objs, (o) => {
        _.each(o.generations, (gen, i) => values[columnsIndexes[gen.genId]] = { obj: gen, isReference: i > 0 });
      });
      return values;
    });

    return { columns, rows: matrix }
  }

  getGrouppingObjectRowHeader(obj, country) {
    // let { genId, countries } = obj;
    return <div className={'text-nowrap text-center'}>
      <CountryFlag countryCode={country} />
    </div>
  }

  getGrouppingObjectColumn(gen) {
    return JSON.stringify(gen)
  }

  getObjectCell(gen) {
    return gen ? gen.genId : '-';
  }
}
